import type { BirdiePhase } from './boardPhase'

/**
 * The golfer capsule's shape, position and animation gates, per phase
 * (B16b — the signed-off mockup).
 *
 * Owner, 2026-09-08: "the capsule should maybe be at the center top of the
 * screen during the betting phase, and when betting is over it moves to the top
 * right like now … remove the countdown number itself, the circle around the
 * avatar seems enough … maybe not that 2 of 3 in, not needed, the visuals
 * should be enough".
 *
 * So the capsule is ONE element that both RESHAPES and MOVES: it holds the
 * centre of the frame while the player is pricing the round, then slides to the
 * top-right corner as the clip starts and stays there through settlement. Which
 * shape, which corner and which animations may run are all a pure function of
 * the round's phase, whether the settled result is revealed yet, and the
 * viewer's motion preference — so the host can pin the box (`cardBoxPx`) from
 * exactly the same value the component styles itself from.
 *
 * B16's third CONTENT state is gone with the owner's "not needed": `final` is
 * the condensed strip plus a gold edge and one sweep, not a different capsule.
 * `golferInitials` lives here because the avatar is the one thing every layout
 * shows.
 */

export type CapsuleLayout =
  /** Betting: head row, the two stat rows, the make-chance row. Centre-top. */
  | 'expanded'
  /** The clip: head row and the make-chance row with the three live slots. */
  | 'condensed'
  /** Settled: the condensed strip with a gold edge and one sweep. */
  | 'final'

/**
 * WHERE the capsule sits. Two boxes, not a range: `cardBoxPx` returns the
 * centre-top box for `centre` and the top-right box for `corner`, and the
 * stylesheet transitions `left`/`top`/`width` between them.
 */
export type CapsulePosition =
  /** Centred at the top of the frame, while betting is open. */
  | 'centre'
  /** Top-right, inside the reserved card zone, from `race_started` onward. */
  | 'corner'

/**
 * Every duration the capsule animates over, in one place, read straight off the
 * signed-off mockup (`scratchpad/birdie-capsule-final.tpl.html`). The
 * stylesheet owns the CSS half of each; these are the values the components
 * hand to `useCountUp` and to the per-segment `transition-delay`, so the two
 * halves cannot drift.
 */

/** The stat bars grow from 0 over this, ms (B16: "400 ms, eased"). */
export const CAPSULE_BAR_MS = 400

/** The make-chance percentage counts up over this, ms (mockup `countUp`). */
export const CAPSULE_COUNT_UP_MS = 1000

/**
 * `Level` and `Green difficulty` count up over this, ms — a beat shorter than
 * the percentage above, so the hero number is the last thing to settle.
 */
export const CAPSULE_STAT_COUNT_UP_MS = 800

/** Each live score slot pops over this as its putt lands, ms. */
export const CAPSULE_SCORE_POP_MS = 250

/** The settled gold sweep across the strip, ms (mockup `sweep`). */
export const CAPSULE_SWEEP_MS = 1100

/** The slide between the two boxes, ms (mockup: `.32s`, one easing). */
export const CAPSULE_SLIDE_MS = 320

/**
 * The coloured fill crosses ONE pill segment in this, ms. The track itself is
 * static grey; only the fill grows, left to right, continuously across the five
 * segments — so segment `i` starts at `i * CAPSULE_PILL_STEP_MS`.
 */
export const CAPSULE_PILL_STEP_MS = 200

/** The green row starts this long after the level row, ms (mockup: `.25s`). */
export const CAPSULE_PILL_GREEN_DELAY_MS = 250

/** Both stat bars are scored out of five (`LEVEL_STARS`, `card.difficulty`). */
export const CAPSULE_BAR_TOTAL = 5

/** …and are drawn as that many pill segments. */
export const CAPSULE_PILL_COUNT = CAPSULE_BAR_TOTAL

export interface CapsuleStateInput {
  phase: BirdiePhase
  /** `boardPhase.settlementVisible` — the settled pattern is on screen. */
  settlementVisible: boolean
  reducedMotion: boolean
  /** `boardPhase.hardLocked` — a hard round past the board's lock. */
  hardLocked?: boolean
}

export interface CapsuleStateView {
  layout: CapsuleLayout
  position: CapsulePosition
  /** The pill fills grow and the two `n/5` numbers count up (expanded only). */
  animateStats: boolean
  /** The rolling-ball scene loops (expanded only; it rests at the lip after). */
  animateScene: boolean
  /** A live score slot drops its ball in as its putt lands. */
  animateScore: boolean
  /** The capsule may EASE between the two boxes rather than jumping. */
  animateSlide: boolean
  /** The settled gold sweep across the strip. */
  sweep: boolean
}

/**
 * Which shape the capsule takes.
 *
 *  - `open` / `closing` — betting: EXPANDED. Everything the player is pricing
 *    the round on is on screen at once.
 *  - `locked` — the clip is playing: CONDENSED, because the live score is the
 *    only thing that changes from here and the stats are spent.
 *  - `settled` — FINAL once the result is actually revealed. Until then the
 *    viewer is still watching putts land through their own playout hold
 *    (`BIRDIE_RESULT_HOLD_MS`), so the capsule stays condensed and keeps
 *    filling the live score: `race_settled` arriving on the wire must never
 *    spoil the third putt.
 *  - `idle` — no round yet. Expanded, so the capsule opens at its full size
 *    rather than growing into it on the first `race_ready`.
 *  - a HARD round past the board's lock (`hardLocked`) — CONDENSED already,
 *    while the round is still `open` or `closing` on the wire: betting is over
 *    at the lock, and the tee-off counts the rest of X to the cut (owner 10-29:
 *    "when it goes into locking the golfer stats slide to the right and then
 *    the tee off").
 *
 * `reducedMotion` never changes the SHAPE, only whether getting there is
 * animated — a viewer who asked for less motion still gets the whole board.
 */
export function capsuleLayout(input: CapsuleStateInput): CapsuleLayout {
  if (input.phase === 'settled') return input.settlementVisible ? 'final' : 'condensed'
  if (input.phase === 'locked') return 'condensed'
  if (input.hardLocked === true && (input.phase === 'open' || input.phase === 'closing')) {
    return 'condensed'
  }
  return 'expanded'
}

/**
 * Where that shape sits.
 *
 * The centre-top box belongs to betting and to betting only: it is the one
 * moment the player is reading the capsule rather than the green, so it takes
 * the middle of the frame. `race_started` moves it to the corner, and it stays
 * there for the clip AND for settlement — the owner asked for one move per
 * round, not two, and a capsule that hopped back to the centre on the settle
 * would cross the picture exactly as the result lands.
 *
 * Derived from the LAYOUT rather than from the phase so the box and the
 * contents can never disagree: `cardBoxPx(layout)` and this function read the
 * same value.
 */
export function capsulePosition(layout: CapsuleLayout): CapsulePosition {
  return layout === 'expanded' ? 'centre' : 'corner'
}

/**
 * The layout, the position, and every animation gate.
 *
 * Each gate is `this layout AND motion is allowed`, so `prefers-reduced-motion`
 * collapses every one of them to its final state on the frame it appears — the
 * pills at their real length, the numbers at the round's values, the ball at
 * the lip of the cup, the score slots filled, the capsule already in its new
 * box. Nothing is HIDDEN by turning motion off, which is the promise the whole
 * board makes; the stylesheet's own `prefers-reduced-motion` block is the
 * second half of it, for the animations CSS starts on its own.
 */
export function capsuleState(input: CapsuleStateInput): CapsuleStateView {
  const layout = capsuleLayout(input)
  const animate = !input.reducedMotion
  return {
    layout,
    position: capsulePosition(layout),
    animateStats: animate && layout === 'expanded',
    animateScene: animate && layout === 'expanded',
    animateScore: animate && layout === 'condensed',
    animateSlide: animate,
    sweep: animate && layout === 'final'
  }
}

/**
 * A bar's fill, 0..1, from an `n / 5` rating.
 *
 * Clamped rather than trusted: `difficulty` is engine-supplied (zod bounds it to
 * 1..5, but a future paytable is one schema edit away) and `levelStars` is 0 for
 * a level the widget does not know — which must draw an EMPTY bar, never a
 * negative `width` that a browser resolves to "auto" and paints full.
 */
export function barFill(value: number, total = CAPSULE_BAR_TOTAL): number {
  if (!Number.isFinite(value) || value <= 0 || total <= 0) return 0
  return Math.min(1, value / total)
}

/**
 * A rating as ONE fill per pill segment, 0..1 each (B16b).
 *
 * The five pills are a static grey track and the coloured fill grows across
 * them left to right — so a 3.2 is three full segments, one filled to a fifth,
 * and one empty, and the partial segment is what makes two "level 3" golfers
 * look different. Returning a fill PER SEGMENT rather than one overall
 * percentage is what lets each segment carry its own colour (the green row
 * ramps yellow → red) and its own `transition-delay`.
 *
 * Clamped per segment and hardened the same way `barFill` is: a non-finite or
 * negative score draws an EMPTY track, never a negative `width` that a browser
 * resolves to "auto" and paints full.
 */
export function pillFills(score: number, count = CAPSULE_PILL_COUNT): readonly number[] {
  const safe = Number.isFinite(score) && score > 0 ? score : 0
  const out: number[] = []
  for (let i = 0; i < count; i++) out.push(Math.max(0, Math.min(1, safe - i)))
  return out
}

/** How many of the three putts have gone in so far — the owner's "total score". */
export function holedCount(marks: readonly string[]): number {
  let count = 0
  for (const mark of marks) if (mark === 'holed') count++
  return count
}

/**
 * Up to two initials for the neutral avatar disc, used when the round carries
 * no headshot URL (or the CDN image fails).
 *
 * First and LAST name part, so "Tom Kim" is TK and "Juan Manuel Ortega" is JO.
 * `charAt` rather than `Array.from`/spread because the build target is es5 and
 * one code unit of a CJK or Thai name is the whole glyph anyway; an emoji-first
 * name degrades to half a surrogate pair, which the disc renders as nothing —
 * acceptable, and never a crash.
 */
export function golferInitials(name: string): string {
  const parts = name.split(/\s+/)
  let first = ''
  let last = ''
  for (const part of parts) {
    if (part.length === 0) continue
    if (first === '') first = part.charAt(0)
    else last = part.charAt(0)
  }
  return (first + last).toUpperCase()
}
